import * as React from "react";
import * as _ from "lodash";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";

import {
  FormRow,
  Select
} from "src/library/components/Forms";
import useTerminology from "src/core/shared/hooks/useTerminology";
import { CustomerMultiSelect } from "src/core/customers/components/CustomerMultiSelect";
import { DropMultiSelect } from "src/core/drops/components/DropMultiSelect";
import { Button } from "src/library/components/Button";
import { BasicModal } from "src/library/components/Modal";
import { ICustomer } from "src/core/customers/api/customers.api";
import { Text } from "src/library/components/Text";
import { IDrop } from "src/core/drops/api/drops.api";
import {
  CreateCustomerBranchFilterAttributes,
  CustomerBranchFilterType
} from "src/core/products/api/products.api";

interface CustomerBranchFiltersModalProps {
  handleSave: (filters: CreateCustomerBranchFilterAttributes[]) => void;
  isOpen: boolean;
  onDismiss: () => void;
  existingFilters: CreateCustomerBranchFilterAttributes[];
  filterType: CustomerBranchFilterType;
  customer?: ICustomer | null;
}

const CustomerBranchFiltersModal: React.ReactFCWithChildren<CustomerBranchFiltersModalProps> = ({
  handleSave, isOpen, onDismiss, existingFilters, filterType, customer
}) => {
  const terminology = useTerminology();
  const [ filterBy, setFilterBy ] = React.useState<"customer" | "drop" | undefined>();
  const [ selectedCustomers, setSelectedCustomers ] = React.useState<ICustomer[]>([]);
  const [ selectedDrops, setSelectedDrops ] = React.useState<IDrop[]>([]);

  React.useEffect(() => {
    if (customer) {
      setFilterBy("drop");
    }
  }, [ customer ]);

  const onSave = React.useCallback(() => {
    let data: CreateCustomerBranchFilterAttributes[] = [];

    if (filterBy === "drop") {
      data = _.map(selectedDrops, d => ({
        branchId: d.id,
        branch: d,
        filterType
      } as CreateCustomerBranchFilterAttributes));
    } else if (filterBy === "customer") {
      data = _.map(selectedCustomers, c => ({
        customerId: c.id,
        customer: c,
        filterType
      } as CreateCustomerBranchFilterAttributes));
    }

    if (!data.length) {
      toast.error("Please add at least one valid filter");
    }

    handleSave(data);
    onDismiss();
  }, [
    filterBy,
    filterType,
    handleSave,
    selectedCustomers,
    selectedDrops,
    onDismiss
  ]);

  return (
    <ModalWrapper
      title="Add a filter"
      open={isOpen}
      onDismiss={() => {
        setSelectedCustomers([]);
        setSelectedDrops([]);
        onDismiss();
      }}
    >
      <div>
        <Text>
          {`Select customer or drops to ${filterType}`}
        </Text>

        {!customer && (
          <FormRow>
            <Select
              label="Filter by"
              name="filterBy"
              as="select"
              unselectedOption={{
                text: "Please select",
                value: ""
              }}
              handleChange={e => {
                setFilterBy(e.currentTarget.value);
                setSelectedDrops([]);
                setSelectedCustomers([]);
              }}
              options={[
                {
                  text: terminology?.customer.singular.text || "customer",
                  value: "customer"
                },
                {
                  text: terminology?.drop.singular.text || "drop",
                  value: "drop"
                }
              ]}
            />
          </FormRow>
        )}

        <FormRow>
          {filterBy === "customer" && (
            <CustomerMultiSelect
              disabledOptions={_.compact(_.map(existingFilters, "customerId"))}
              onCustomersChange={({ fullCustomersSelected }) => setSelectedCustomers(fullCustomersSelected)}
            />
          )}

          {filterBy === "drop" && (
            <DropMultiSelect
              disabledOptions={_.compact(_.map(existingFilters, "branchId"))}
              onDropsChange={({ fullDropsSelected }) => setSelectedDrops(fullDropsSelected)}
              isHidden={drop => {
                if (customer) {
                  return customer.id === drop.customerId;
                }

                return true;
              }}
            />
          )}
        </FormRow>

        <Button
          className="submit-button"
          disabled={!selectedCustomers.length && !selectedDrops.length}
          onClick={() => onSave()}
        >
          Save filter
        </Button>
      </div>
    </ModalWrapper>
  );
};

export default CustomerBranchFiltersModal;

const ModalWrapper = styled(BasicModal)`
  ${({ theme }) => css`
    max-height: 100vh;
    overflow-y: scroll;

    .submit-button {
      margin-top: ${theme.spacing.margins.md};
    }
  `}
`;
